import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { Filter, FilterType } from '../../../../types/types';
import settingsParams from '../../settingsParams';
import { AsyncComponentDataStatus } from '../../state/types';
import { ViewModelSubscription } from '../initViewModelSubscriptions';
import { WidgetViewModelFactory } from '../viewModel';

export type FiltersViewModel = {
  filters: Filter[];
  filterByText: string;
};

export const createFiltersViewModel: WidgetViewModelFactory<
  FiltersViewModel
> = ({
  state: { selectedFilters, services, locations, staffMembers },
  flowAPI,
}) => {
  const {
    settings,
    environment: { isEditor },
    translations: { t },
  } = flowAPI;
  if (
    isEditor &&
    services.status === AsyncComponentDataStatus.IDLE &&
    !Object.values(services.data).length
  ) {
    return createDummyFiltersViewModel({ flowAPI });
  }
  const filters = [];

  if (services.status === AsyncComponentDataStatus.IDLE) {
    const servicesFilter = {
      type: FilterType.SERVICE,
      label: settings.get(settingsParams.serviceLabel),
      options: Object.values(services.data).map((service) => ({
        selected: !!selectedFilters?.serviceIds?.includes(service.id!),
        id: service.id!,
        value: service.name!,
      })),
    };

    if (servicesFilter.options.length > 1) {
      filters.push(servicesFilter);
    }
  }

  if (locations.status === AsyncComponentDataStatus.IDLE) {
    const selectedLocationToDisplay = settings.get(
      settingsParams.selectedLocations,
    );
    const locationsFilter = {
      type: FilterType.LOCATION,
      label: settings.get(settingsParams.locationLabel),
      options: Object.values(locations.data)
        .filter((location) => {
          // If no selectedLocationToDisplay or it's empty, show all locations
          if (
            !selectedLocationToDisplay ||
            !Array.isArray(selectedLocationToDisplay) ||
            selectedLocationToDisplay.length === 0
          ) {
            return true;
          }
          return (
            location.id &&
            selectedLocationToDisplay.some((id) => id === location.id)
          );
        })
        .map((location) => ({
          selected: !!selectedFilters?.businessLocationIds?.includes(
            location.id!,
          ),
          id: location.id!,
          value: location.business?.name!,
        })),
    };

    if (locationsFilter.options.length > 1) {
      filters.push(locationsFilter);
    }
  }

  if (staffMembers.status === AsyncComponentDataStatus.IDLE) {
    const staffMembersFilter = {
      type: FilterType.STAFF_MEMBER,
      label: settings.get(settingsParams.staffMemberLabel),
      options: Object.values(staffMembers.data).map((staffMember) => ({
        selected: !!selectedFilters?.staffMemberIds?.includes(
          staffMember.resourceId!,
        ),
        id: staffMember.resourceId!,
        value: staffMember.name!,
      })),
    };
    if (staffMembersFilter.options.length > 1) {
      filters.push(staffMembersFilter);
    }
  }

  return {
    filters,
    filterByText: t('daily-agenda.filters.filter-by'),
  };
};

export const subscribeToFiltersViewModel: ViewModelSubscription = ({
  setViewModel,
  subscribe,
  flowAPI,
  getState,
}) => {
  subscribe(
    ({ selectedFilters, services, locations, staffMembers }) => ({
      selectedFilters,
      servicesStatus: services.status,
      locationsStatus: locations.status,
      staffMembersStatus: staffMembers.status,
    }),
    () => {
      const filtersViewModel = createFiltersViewModel({
        state: getState(),
        flowAPI,
      });
      setViewModel({ filtersViewModel });
    },
  );
};

export const createDummyFiltersViewModel = ({
  flowAPI: {
    settings,
    translations: { t },
  },
}: {
  flowAPI: ControllerFlowAPI;
}): FiltersViewModel => ({
  filters: [
    {
      type: FilterType.SERVICE,
      label: settings.get(settingsParams.serviceLabel),
      options: [
        {
          selected: false,
          id: 'service1',
          value: t('daily-agenda.dummy.service-name'),
        },
        {
          selected: false,
          id: 'service2',
          value: t('daily-agenda.dummy.service-name'),
        },
      ],
    },
    {
      type: FilterType.LOCATION,
      label: settings.get(settingsParams.locationLabel),
      options: [
        {
          selected: false,
          id: 'location1',
          value: t('daily-agenda.dummy.location'),
        },
        {
          selected: false,
          id: 'location2',
          value: t('daily-agenda.dummy.location'),
        },
      ],
    },
    {
      type: FilterType.STAFF_MEMBER,
      label: settings.get(settingsParams.staffMemberLabel),
      options: [
        {
          selected: false,
          id: 'staff1',
          value: t('daily-agenda.dummy.staff-name'),
        },
        {
          selected: false,
          id: 'staff2',
          value: t('daily-agenda.dummy.staff-name'),
        },
      ],
    },
  ],
  filterByText: t('daily-agenda.filters.filter-by'),
});
